/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { generateIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { celebrate } from "@/lib/celebrate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, UserRound, Pencil, Calendar, School, HeartPulse, Sparkles, PhoneCall, Shirt, Footprints, StickyNote, ChevronLeft, Pill, AlertTriangle, Droplet, Clock, Phone, Stethoscope, MapPin, IdCard, Lock, Archive, History, RotateCcw, ArchiveRestore, ShieldCheck, Download, MessageCircle, Check, X, Users, MessageSquare, Wallet } from "lucide-react";

// Inline icon helpers for the per-child quick-action row.
const ChildMessagesIcon = () => <MessageSquare className="w-3 h-3" strokeWidth={1.8} />;
const ChildExpensesIcon = () => <Wallet className="w-3 h-3" strokeWidth={1.8} />;
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { InviteCoparentDialog } from "@/components/children/InviteCoparentDialog";
import { ChildProposalsReviewDialog } from "@/components/children/ChildProposalsReviewDialog";
import {
  ageFromDob,
  ageDetailFromDob,
  relativeDuration,
  formatHistoryAt,
  SECTION_LABELS,
  ChildSharingLine,
  SectionLastUpdated,
} from "@/components/children/ChildrenHelpers";
import { getParentColor } from "@/lib/parentColor";
import { ARRANGEMENT_TYPES, ARRANGEMENT_LABEL } from "@/lib/parentingArrangements";

const emptyForm = {
  name: "", dob: "", color: "#1F9FA4",
  // Medical
  medical: "", allergies: "", medication: "", medication_instructions: "", blood_type: "",
  gp_name: "", gp_address: "", gp_phone: "", medical_notes: "",
  // School
  school: "", teacher_name: "", school_dropoff_time: "", school_pickup_time: "", school_phone: "", school_notes: "",
  // Daily + contact
  routines: "", emergency_contact: "",
  clothing_size: "", shoe_size: "", notes: "",
  // v77.99.9 — Per-child arrangement override. Empty string = inherit
  // from household default / data-derived inference.
  parenting_arrangement: "",
};
const BLOOD_TYPES = ["", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];

// Phase 3 Delivery 2 — locked sections + their fields. Mirrors
// `CHILD_FIELD_SECTION` on the backend; co-parents must submit a
// proposal to change anything in here. Keep in sync with
// `/app/backend/server.py:CHILD_FIELD_SECTION`.
const LOCKED_SECTION_FIELDS = {
  medical: [
    "allergies", "medication", "medication_instructions", "blood_type",
    "medical", "gp_name", "gp_address", "gp_phone", "medical_notes",
  ],
  school: [
    "school", "teacher_name", "school_dropoff_time", "school_pickup_time",
    "school_phone", "school_notes",
  ],
  emergency: ["emergency_contact"],
};
const LOCKED_FIELD_LABELS = {
  allergies: "Allergies",
  medication: "Medication",
  medication_instructions: "Medication instructions",
  blood_type: "Blood type",
  medical: "Other medical notes",
  gp_name: "GP name",
  gp_address: "GP address",
  gp_phone: "GP phone number",
  medical_notes: "Medical notes",
  school: "School name",
  teacher_name: "Teacher's name",
  school_dropoff_time: "Drop-off time",
  school_pickup_time: "Pick-up time",
  school_phone: "School contact phone",
  school_notes: "School notes / instructions",
  emergency_contact: "Emergency contact",
};
const LOCKED_SECTION_LABELS = {
  medical: "Medical",
  school: "School",
  emergency: "Emergency",
};
// Pre-filtered list used by the form's <Select>; module-scope so we don't
// re-filter on every render of the child dialog.
const BLOOD_TYPE_OPTIONS = BLOOD_TYPES.filter(Boolean);
// v65.2 — curated colour palette. Mirrored from the backend's
// `CHILD_COLOR_PALETTE`. Don't edit here without updating
// `/app/backend/server.py:CHILD_COLOR_PALETTE` — the backend
// `_normalise_child_color` helper snaps off-palette values to default
// teal on save, so a frontend-only addition would silently lose the
// user's pick.
const COLOR_PALETTE = [
  "#1F9FA4",  // Teal (default)
  "#E76F51",  // Coral
  "#E9B44C",  // Amber
  "#88A47C",  // Sage
  "#9D8DC8",  // Lavender
  "#C44569",  // Berry
  "#6BA5BD",  // Sky
  "#B98856",  // Ochre
  "#D88FA0",  // Rose
  "#5C8A8A",  // Slate teal
];

const COLOR_NAMES = {
  "#1F9FA4": "Teal",
  "#E76F51": "Coral",
  "#E9B44C": "Amber",
  "#88A47C": "Sage",
  "#9D8DC8": "Lavender",
  "#C44569": "Berry",
  "#6BA5BD": "Sky",
  "#B98856": "Ochre",
  "#D88FA0": "Rose",
  "#5C8A8A": "Slate teal",
};

// v77.99.77 — `ageFromDob`, `ageDetailFromDob`, `relativeDuration`,
// `formatHistoryAt`, `SECTION_LABELS`, `ChildSharingLine`, and
// `SectionLastUpdated` were extracted to
// `components/children/ChildrenHelpers.jsx`. They had no page-local
// dependencies and are now reusable from any future child-focused
// surface.

// v69 — render an old/new value in the history timeline. Empty → em-dash;
// long values truncated. Colours render as a small swatch rather than
// raw hex codes (the user picks colours visually, not by hex).
function HistoryValue({ field, value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-[#6F828A] italic">empty</span>;
  }
  if (field === "color") {
    return (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <span
          className="inline-block w-3.5 h-3.5 rounded-full ring-1 ring-[#D9E1E3]"
          style={{ backgroundColor: value }}
        />
        <span>{COLOR_NAMES[value?.toUpperCase()] || COLOR_NAMES[value] || value}</span>
      </span>
    );
  }
  const s = String(value);
  if (s.length > 80) return <span title={s}>{s.slice(0, 80)}…</span>;
  return <span>{s}</span>;
}

// v77.99.77 — `formatHistoryAt`, `SECTION_LABELS`, and
// `SectionLastUpdated` moved to `components/children/ChildrenHelpers.jsx`
// alongside the other pure helpers. Imported at the top of this file.

export default function Children() {
  const { user, members: authMembers, isMediator } = useAuth();
  const { selectedChildId, refetch: refetchChildScope } = useChildScope();
  const isPrimaryParent = !!user?.is_primary_parent;
  // v77.48 — Per-child managing-parent helper. Mirrors the backend's
  // `_is_child_managing_parent`: a user manages a child when they
  // created it (`created_by == user.id`) or, for legacy children
  // without a `created_by` tag, when they're the household creator.
  // Critically: brand-new children (the "Add child" dialog before
  // submit) treat the current user as the managing-parent-to-be so
  // the locked sections aren't artificially gated for someone who
  // is about to BECOME the managing parent on save.
  const isManagingParentOf = (child) => {
    if (!child) return false;
    if (child.isNew) return true;
    const creator = child.created_by;
    if (creator) return creator === user?.id;
    return !!user?.is_primary_parent;
  };
  const householdMembers = authMembers || [];
  const [kids, setKids] = useState([]);
  // v77.99.150 (iter 122.loading-states) — Prevents "No children added
  // yet." from appearing before the first /children response returns.
  const [kidsLoading, setKidsLoading] = useState(true);
  // v77.48 — Role grouping: which children the current user MANAGES vs
  // which they're a co-parent on. Sourced from /api/me/child-roles so
  // the same data feeds both the Settings card (v77.47) and this page.
  const [roleGroups, setRoleGroups] = useState({ managedIds: new Set(), coparentIds: new Set() });
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | existing-child
  const [form, setForm] = useState(emptyForm);
  // v66 — co-parents panel inside the view dialog. Lazy-loads when the
  // dialog opens so we don't hammer the backend for every child card.
  const [coparentsByChild, setCoparentsByChild] = useState({});
  const [coparentsEditing, setCoparentsEditing] = useState(false);
  const [coparentsDraft, setCoparentsDraft] = useState([]);  // array of user_ids being toggled
  // Snapshot of the form taken when the edit dialog opens so we can reliably
  // detect whether the user has made any changes — used to gate accidental
  // dialog-close (mobile users tapping outside a Select-portal item used to
  // dismiss the entire dialog and lose all entered data).
  const initialFormSnapshot = useRef("");
  // v77.48 — Per-child managing-parent gates for the dialogs. Derived
  // from `isManagingParentOf` against the child currently being
  // edited / viewed. Replaces the household-wide `isPrimaryParent`
  // gate inside the dialogs so blended-family co-parents can edit
  // medical / school / emergency info on children THEY manage.
  const canManageEditing = isManagingParentOf(editing);
  const canManageViewing = isManagingParentOf(viewing);

  // v69 — change history panel state. Keyed by child id so opening the
  // panel for one child doesn't blow away another child's cached data.
  const [historyByChild, setHistoryByChild] = useState({});
  const [sectionsByChild, setSectionsByChild] = useState({});
  const [historyOpenFor, setHistoryOpenFor] = useState(null); // child id
  const [historyLoading, setHistoryLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Confirm dialogs for destructive actions (archive / revert) — neutral
  // tone, "this action is logged and visible to the other parent".
  const [confirmAction, setConfirmAction] = useState(null);
  // shape: { kind: "archive" | "restore" | "revert", child, entry? }

  const load = async () => {
    try {
      const { data } = await api.get("/children");
      setKids(data);
      // Keep viewing dialog in sync if the record changed
      if (viewing) {
        const fresh = data.find((c) => c.id === viewing.id);
        if (fresh) setViewing(fresh);
      }
      // v77.48 — Role grouping. Failure here is non-fatal — fall back to
      // a single un-grouped list rather than blocking the page.
      try {
        const { data: roles } = await api.get("/me/child-roles");
        setRoleGroups({
          managedIds: new Set((roles?.managed || []).map((c) => c.id)),
          coparentIds: new Set((roles?.coparenting || []).map((c) => c.id)),
        });
      } catch {
        setRoleGroups({ managedIds: new Set(), coparentIds: new Set() });
      }
    } finally {
      setKidsLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    const next = { ...emptyForm };
    setForm(next);
    initialFormSnapshot.current = JSON.stringify(next);
    setEditing({ isNew: true });
  };
  const openEdit = (c) => {
    const next = { ...emptyForm, ...c };
    setForm(next);
    initialFormSnapshot.current = JSON.stringify(next);
    setEditing(c);
    setViewing(null);
  };
  const openView = (c) => setViewing(c);

  // v66 — per-child co-parent management. Loads who's linked to a
  // child, lets the primary parent toggle co-parent membership, and
  // saves the new set via PUT /api/children/{id}/coparents.
  // Re-load whenever a child becomes the active "viewing" target.
  useEffect(() => {
    if (viewing?.id) {
      // Always refetch on open so changes from another tab are reflected.
      api.get(`/children/${viewing.id}/coparents`)
        .then((r) => setCoparentsByChild((prev) => ({ ...prev, [viewing.id]: r.data || [] })))
        .catch(() => {});
    }
  }, [viewing?.id]);

  const startEditCoparents = () => {
    if (!viewing) return;
    const current = (coparentsByChild[viewing.id] || [])
      .filter((c) => !c.is_primary_parent)
      .map((c) => c.id);
    setCoparentsDraft(current);
    setCoparentsEditing(true);
  };

  const toggleCoparentDraft = (userId) => {
    setCoparentsDraft((prev) => prev.includes(userId)
      ? prev.filter((u) => u !== userId)
      : [...prev, userId]);
  };

  const saveCoparents = async () => {
    if (!viewing) return;
    try {
      const { data } = await api.put(`/children/${viewing.id}/coparents`, { user_ids: coparentsDraft });
      setCoparentsByChild((prev) => ({ ...prev, [viewing.id]: data || [] }));
      setCoparentsEditing(false);
      toast.success("Co-parent access updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    }
  };

  // v72 — Per-child invite. Primary parent generates a token granting
  // access to one or more children (defaults to just the active child;
  // sibling check-boxes let the inviter widen the scope before sending).
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteChildIds, setInviteChildIds] = useState([]);
  const [inviteResult, setInviteResult] = useState(null);  // { token, join_url }
  const [inviteSending, setInviteSending] = useState(false);

  const openInvite = () => {
    if (!viewing) return;
    setInviteEmail("");
    setInviteChildIds([viewing.id]);  // default = single child (locked decision)
    setInviteResult(null);
    setInviteOpen(true);
  };

  // v77.22.5 — Quick-invite from the Children list card. Opens the
  // same invite flow as the detail view but skips the
  // "open child → tap Invite" round-trip. The user spotted the gap
  // ("No co-parent assigned") and we close it in one tap from the
  // same card.
  const quickInviteFromCard = (child) => (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setViewing(child);
    setInviteEmail("");
    setInviteChildIds([child.id]);
    setInviteResult(null);
    setInviteOpen(true);
  };

  const toggleInviteChild = (childId) => {
    if (childId === viewing?.id) return;  // URL child is always included
    setInviteChildIds((prev) => prev.includes(childId)
      ? prev.filter((c) => c !== childId)
      : [...prev, childId]);
  };

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    if (!viewing) return;
    setInviteSending(true);
    try {
      // v77.99.18 — Idempotency-Key on co-parent invite: prevents
      // duplicate invite + duplicate emails when the user double-taps
      // or the network retries the POST.
      const { data } = await api.post(
        `/children/${viewing.id}/invite`,
        {
          child_ids: inviteChildIds,
          email: inviteEmail.trim() || null,
        },
        { headers: idempotencyHeader(generateIdempotencyKey()) },
      );
      setInviteResult({
        token: data.invite?.token,
        join_url: data.join_url,
        emailed: !!data.email?.sent,
      });
      if (data.email?.sent) toast.success("Invite emailed");
      else toast.success("Invite link ready");
      // v77.48 — First co-parent invite milestone. Fires once per
      // user. Quietly skipped on subsequent invites.
      celebrate({
        key: "first-coparent-invite",
        userId: user?.id,
        title: "First co-parent invited",
        body: data.email?.sent
          ? "We've emailed them an invite — once they accept, you'll start sharing this child's records and activity feed."
          : "Share the invite link with them. Once they accept, you'll start sharing this child's records and activity feed.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create invite");
    } finally {
      setInviteSending(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteResult?.join_url) return;
    try {
      await navigator.clipboard.writeText(inviteResult.join_url);
      toast.success("Invite link copied");
    } catch (err) {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  // True if the form differs from the snapshot taken when the dialog opened.
  const isFormDirty = () => JSON.stringify(form) !== initialFormSnapshot.current;

  // Single source of truth for closing the edit dialog. Confirms with the
  // user before discarding unsaved changes. Used by the X button, Cancel
  // button, Esc key, and any other path that wants to dismiss the dialog.
  const tryCloseEdit = () => {
    if (isFormDirty() && !window.confirm("Discard your changes? Any unsaved edits will be lost.")) {
      return;
    }
    setEditing(null);
  };

  // v77.22.6 — Track in-flight Add/Edit Child submission so the Save
  // button is disabled (and the form ignores rapid resubmits) while
  // the backend is creating the record. Prevents the "Child 5
  // appearing twice" duplicate-on-double-tap class of bug.
  const [savingChild, setSavingChild] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (savingChild) return;  // ignore rapid resubmit
    setSavingChild(true);
    try {
      if (editing?.isNew) await api.post("/children", form);
      else await api.put(`/children/${editing.id}`, form);
      toast.success(editing?.isNew ? "Child added" : "Profile updated");
      setEditing(null);
      await load();
      // v77.48 — Refresh the global child-scope (top-right toggle)
      // so a freshly added/renamed child appears in the picker
      // without a hard reload. Previously the local `kids` state
      // updated but the dropdown stayed stale, so the new child
      // showed on the cards but was missing from the "Viewing"
      // selector (user-reported regression).
      try { await refetchChildScope?.(); } catch (e) { /* non-fatal */ }
      // v69 — refresh history if it's currently open for this child
      // so the new diff appears immediately at the top of the timeline.
      if (historyOpenFor && (editing?.id === historyOpenFor || !editing?.isNew)) {
        loadHistory(historyOpenFor);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSavingChild(false); }
  };

  // v69 — change history. Loads on demand (from the "View history"
  // button or after any successful save) so we don't spam the backend
  // for every card render.
  const loadHistory = async (childId) => {
    if (!childId) return;
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/children/${childId}/history`);
      setHistoryByChild((prev) => ({ ...prev, [childId]: data.history || [] }));
      setSectionsByChild((prev) => ({ ...prev, [childId]: data.sections || {} }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async (child) => {
    setHistoryOpenFor(child.id);
    await loadHistory(child.id);
  };

  // Phase 3 Delivery 2 — downloadable per-child change history PDF.
  // Streams from the backend; the browser handles save-as via blob.
  const downloadHistoryPdf = async (childId) => {
    try {
      const res = await fetch(`${API}/children/${childId}/history.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `child_${childId}_history.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Change history downloaded");
    } catch (err) {
      toast.error("Couldn't download history PDF");
    }
  };

  // Phase 3 Delivery 2 — proposed changes (Suggest update). Co-parents
  // submit proposals on locked sections; the primary parent reviews
  // them in a dedicated dialog. We track:
  //  - proposalChild + proposalSection — context for the "Suggest update"
  //    dialog opened from a co-parent's edit form.
  //  - proposalDrafts — { [field]: { new_value, note } } — uncommitted
  //    edits inside the Suggest dialog.
  //  - pendingProposals — list of pending proposals across all kids (for
  //    the primary parent's review pane).
  //  - proposalReviewOpen — whether the primary parent's review dialog
  //    is showing.
  const [proposalChild, setProposalChild] = useState(null);
  const [proposalSection, setProposalSection] = useState(null);
  const [proposalDrafts, setProposalDrafts] = useState({});
  const [proposalNote, setProposalNote] = useState("");
  const [proposalSubmitting, setProposalSubmitting] = useState(false);
  const [pendingProposals, setPendingProposals] = useState([]);
  const [proposalReviewOpen, setProposalReviewOpen] = useState(false);
  const [proposalDecisionBusy, setProposalDecisionBusy] = useState(false);

  const loadPendingProposals = async () => {
    // v77.48 — Load proposals per visible child. The backend already
    // restricts what's returned (managing parents see all proposals
    // on their managed children; co-parents see only their own
    // submissions), so we don't need a household-primary gate here.
    try {
      const all = [];
      for (const c of kids) {
        const { data } = await api.get(`/children/${c.id}/proposals?status=pending`);
        for (const p of data || []) all.push({ ...p, child_name: c.name, child_color: c.color });
      }
      setPendingProposals(all);
    } catch (err) {
      // non-critical — fail quiet
    }
  };

  useEffect(() => {
    if (kids.length > 0) loadPendingProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kids.length]);

  const openSuggestUpdate = (child, section) => {
    setProposalChild(child);
    setProposalSection(section);
    // Pre-seed drafts with the current values so co-parents can edit
    // in-place — typical "I want this changed" flow.
    const drafts = {};
    for (const f of LOCKED_SECTION_FIELDS[section] || []) {
      drafts[f] = { new_value: child[f] || "" };
    }
    setProposalDrafts(drafts);
    setProposalNote("");
  };

  const closeSuggestUpdate = () => {
    setProposalChild(null);
    setProposalSection(null);
    setProposalDrafts({});
    setProposalNote("");
  };

  const submitProposals = async () => {
    if (!proposalChild || !proposalSection) return;
    const fields = LOCKED_SECTION_FIELDS[proposalSection] || [];
    const changed = fields.filter((f) => (proposalDrafts[f]?.new_value || "") !== (proposalChild[f] || ""));
    if (changed.length === 0) { toast.error("Change at least one field before sending."); return; }
    setProposalSubmitting(true);
    try {
      for (const f of changed) {
        await api.post(`/children/${proposalChild.id}/propose`, {
          field: f,
          new_value: proposalDrafts[f]?.new_value || "",
          note: proposalNote.trim() || null,
        });
      }
      toast.success(`Sent ${changed.length} suggestion${changed.length === 1 ? "" : "s"} for the managing parent to review.`);
      closeSuggestUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send suggestion");
    } finally {
      setProposalSubmitting(false);
    }
  };

  const decideProposal = async (proposal, action) => {
    setProposalDecisionBusy(true);
    try {
      await api.post(`/children/${proposal.child_id}/proposals/${proposal.id}/${action}`, {});
      toast.success(action === "approve" ? "Change applied" : "Suggestion declined");
      await loadPendingProposals();
      // Also refresh history & child data in case the change applied.
      if (action === "approve") {
        await load();
        await loadHistory(proposal.child_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update");
    } finally {
      setProposalDecisionBusy(false);
    }
  };

  // Lazy-load section "last updated" data when the view dialog opens —
  // small payload, single request, keeps the badge accurate without
  // forcing the user to open the history panel.
  useEffect(() => {
    if (viewing?.id && !sectionsByChild[viewing.id]) {
      loadHistory(viewing.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewing?.id]);

  const archiveChild = async (child) => {
    if (!child) return;
    setArchiving(true);
    try {
      await api.delete(`/children/${child.id}`);
      toast.success(`${child.name}'s profile archived`);
      setConfirmAction(null);
      setViewing(null);
      await load();
      try { await refetchChildScope?.(); } catch (e) { /* non-fatal */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't archive profile");
    } finally {
      setArchiving(false);
    }
  };

  const restoreChild = async (child) => {
    if (!child) return;
    setArchiving(true);
    try {
      await api.post(`/children/${child.id}/restore`);
      toast.success(`${child.name}'s profile restored`);
      setConfirmAction(null);
      await load();
      try { await refetchChildScope?.(); } catch (e) { /* non-fatal */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't restore profile");
    } finally {
      setArchiving(false);
    }
  };

  const revertChange = async (child, entry) => {
    if (!child || !entry) return;
    try {
      await api.post(`/children/${child.id}/history/${entry.id}/revert`);
      toast.success(`${entry.label || "Change"} reverted`);
      setConfirmAction(null);
      await load();
      await loadHistory(child.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revert");
    }
  };

  return (
    <div className="space-y-6" data-testid="children-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Shared profiles</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1">Children</h1>
          <p className="text-sm text-[#6F828A] mt-1">Tap a card to view full details. Use Edit when you need to change something.</p>
        </div>
        <div className="flex items-center gap-2">
          {isPrimaryParent && pendingProposals.length > 0 && !isMediator && (
            <Button
              variant="outline"
              onClick={() => setProposalReviewOpen(true)}
              className="border-[#825A41]/40 text-[#825A41] hover:bg-[#FAF5EE] rounded-sm"
              data-testid="review-suggestions-button"
            >
              <ShieldCheck className="w-4 h-4 mr-1.5" />
              Review {pendingProposals.length} suggestion{pendingProposals.length === 1 ? "" : "s"}
            </Button>
          )}
          {!isMediator && (
            <Button onClick={openNew} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="add-child-button">
              <Plus className="w-4 h-4 mr-2" /> Add child
            </Button>
          )}
        </div>
      </div>

      {kidsLoading && kids.length === 0 ? (
        <div className="border border-[#D9E1E3] bg-white rounded-md p-12 text-center" data-testid="children-loading">
          <UserRound className="w-8 h-8 mx-auto text-[#6F828A] opacity-60" strokeWidth={1.5} />
          <p className="mt-3 text-[#6F828A]">Loading your children…</p>
        </div>
      ) : kids.length === 0 ? (
        <div className="border border-[#D9E1E3] bg-white rounded-md p-12 text-center" data-testid="children-empty">
          <UserRound className="w-8 h-8 mx-auto text-[#6F828A]" strokeWidth={1.5} />
          <p className="mt-3 text-[#6F828A]">No children added yet.</p>
        </div>
      ) : (() => {
        // v77.48 — Role-aware grouping. We render TWO lists (Managed by
        // you + Co-parenting) when both roles exist, otherwise we fall
        // back to a single ungrouped list to avoid noise. Filters and
        // selected-child scope are honoured the same way for each group.
        const renderCard = (c) => {
          const ageDetail = ageDetailFromDob(c.dob);
          const isArchived = !!c.archived_at;
          return (
            <li key={c.id} data-testid={`child-${c.id}`}>
              {/* v77.48 — Card layout reworked: the Expenses link used
                  to be `absolute bottom-3 right-3` and overlapped the
                  "No co-parent assigned · Invite a co-parent" line on
                  narrow viewports. Now the link sits in normal flow,
                  right-aligned via flex, so the sharing text can wrap
                  cleanly underneath without clipping. */}
              <div className={`border border-[#D9E1E3] bg-white rounded-md overflow-hidden ${isArchived ? "opacity-70" : ""}`}>
                <button type="button" onClick={() => openView(c)} className="w-full text-left p-5 hover:bg-[#EEF3F4] transition-colors" data-testid={`view-child-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="w-4 h-4 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: c.color || "#1F9FA4" }} data-testid={`child-color-${c.id}`}></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-heading text-xl font-medium truncate">{c.name}</span>
                        {ageDetail && (
                          <span className="text-xs text-[#6F828A]" data-testid={`child-age-${c.id}`}>
                            {ageDetail}
                          </span>
                        )}
                        {isArchived && (
                          <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5" data-testid={`child-archived-badge-${c.id}`}>
                            Archived
                          </span>
                        )}
                      </div>
                      {c.dob && <div className="text-xs text-[#6F828A] font-mono-data mt-0.5">DOB · {formatDate(c.dob)}</div>}
                      {isManagingParentOf(c) && <ChildSharingLine child={c} onAddCoparent={quickInviteFromCard(c)} />}
                      {/* v77.62 — Children listed under "Co-parenting"
                          must surface the managing parent so the user
                          immediately understands which relationship
                          applies — useful when a single co-parent has
                          ties to multiple unrelated households. We
                          resolve the managing parent's name from the
                          loaded household members list (sourced from
                          `c.created_by`, the canonical managing-parent
                          tag since v77.46). Falls back gracefully to
                          a generic "another parent" label when the
                          name can't be resolved (e.g. archived user
                          or pre-tag legacy children). */}
                      {!isManagingParentOf(c) && roleGroups.coparentIds.has(c.id) && (() => {
                        // v77.62.1 — Prefer the server-enriched
                        // `managing_parent` field (added in v77.62
                        // for blended-household scenarios where the
                        // managing parent of a shared child may
                        // belong to a different household than the
                        // viewer). Fall back to local `householdMembers`
                        // resolution for backwards compatibility, and
                        // finally to a generic "another parent"
                        // string when no name is recoverable.
                        let managingName = c.managing_parent?.name || c.managing_parent?.email;
                        if (!managingName) {
                          const creatorId = c.created_by;
                          const localMatch = creatorId
                            ? householdMembers.find((m) => m.id === creatorId)
                            : null;
                          managingName = localMatch?.name || localMatch?.email || "another parent";
                        }
                        return (
                          <div
                            className="mt-2 text-[11px] text-[#116B6E] inline-flex items-center gap-1.5"
                            data-testid={`child-coparenting-with-${c.id}`}
                            title={`Managing parent: ${managingName}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#1F9FA4]" />
                            <span>
                              Co-parenting with{" "}
                              <span className="font-medium text-[#0F5658]">{managingName}</span>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </button>
                <div className="px-5 pb-3 flex justify-end gap-1.5">
                  {/* v77.99.61 — Three quick actions per child card:
                      Profile (tap the card body), Messages, Expenses.
                      Previously this row only surfaced Expenses, which
                      felt arbitrary — parents most often want to open
                      messages or the profile detail from this page. */}
                  <Link
                    to={`/app/messages?child_id=${encodeURIComponent(c.id)}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#4A6170] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-2 py-1 hover:bg-[#E0E7E9] transition-colors"
                    data-testid={`open-child-messages-${c.id}`}
                    aria-label={`Open ${c.name}'s messages`}
                  >
                    <ChildMessagesIcon /> Messages
                  </Link>
                  <Link
                    to={`/app/children/${c.id}/expenses`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#116B6E] bg-[#E0F2F2] border border-[#A6D7D9] rounded-sm px-2 py-1 hover:bg-[#C8E4E5] transition-colors"
                    data-testid={`open-child-expenses-${c.id}`}
                    aria-label={`Open ${c.name}'s expenses page`}
                  >
                    <ChildExpensesIcon /> Expenses
                  </Link>
                </div>
              </div>
            </li>
          );
        };
        const visibleKids = kids.filter((c) => !selectedChildId || c.id === selectedChildId);
        const managed = visibleKids.filter((c) => roleGroups.managedIds.has(c.id));
        const coparenting = visibleKids.filter((c) => roleGroups.coparentIds.has(c.id));
        const ungrouped = visibleKids.filter(
          (c) => !roleGroups.managedIds.has(c.id) && !roleGroups.coparentIds.has(c.id),
        );
        // Show grouped headers only when the user actually has BOTH
        // roles. Single-role users see the unchanged flat list.
        const useGrouped = managed.length > 0 && coparenting.length > 0;
        if (!useGrouped) {
          return (
            <ul className="grid md:grid-cols-2 gap-4" data-testid="children-list">
              {visibleKids.map(renderCard)}
            </ul>
          );
        }
        return (
          <div className="space-y-7" data-testid="children-list">
            {managed.length > 0 && (
              <div data-testid="children-group-managed">
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Managed by you ({managed.length})
                </div>
                <ul className="grid md:grid-cols-2 gap-4">
                  {managed.map(renderCard)}
                </ul>
              </div>
            )}
            {coparenting.length > 0 && (
              <div data-testid="children-group-coparenting">
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Co-parenting ({coparenting.length})
                </div>
                <ul className="grid md:grid-cols-2 gap-4">
                  {coparenting.map(renderCard)}
                </ul>
              </div>
            )}
            {ungrouped.length > 0 && (
              <div data-testid="children-group-other">
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2">
                  Other ({ungrouped.length})
                </div>
                <ul className="grid md:grid-cols-2 gap-4">
                  {ungrouped.map(renderCard)}
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* VIEW-ONLY dialog */}
      <Dialog open={!!viewing && !editing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-white border-[#D9E1E3] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="child-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="w-5 h-5 rounded-full" style={{ backgroundColor: viewing.color || "#1F9FA4" }}></span>
                  <div>
                    <DialogTitle className="font-heading text-2xl" data-testid="child-view-name">{viewing.name}</DialogTitle>
                    <div className="text-xs text-[#6F828A] font-mono-data">
                      {viewing.dob ? `${formatDate(viewing.dob)} · ${ageFromDob(viewing.dob) ?? "—"} yrs` : "No date of birth"}
                    </div>
                  </div>
                  {viewing.archived_at && (
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-2 py-1 ml-auto inline-flex items-center gap-1.5" data-testid="child-view-archived-badge">
                      <Archive className="w-3 h-3" /> Archived {formatHistoryAt(viewing.archived_at)}
                    </span>
                  )}
                </div>
              </DialogHeader>
              <div className="space-y-3 mt-2" data-testid="child-view-details">
                <InfoRow icon={Calendar} label="Date of birth" value={viewing.dob ? formatDate(viewing.dob) : null} />
                {/* Read-only colour display in the profile view. The
                    primary parent changes the colour from the Edit
                    dialog, which gates the interactive picker on
                    `is_primary_parent`. Co-parents only see the swatch.
                    No "view-only" label needed since both roles see
                    the same read-only swatch here. */}
                <div className="border border-[#D9E1E3] rounded-sm p-3" data-testid="child-color-display">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Colour
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="w-6 h-6 rounded-full ring-1 ring-[#D9E1E3]"
                      style={{ backgroundColor: viewing.color || COLOR_PALETTE[0] }}
                      data-testid="child-color-swatch"
                    />
                    <span className="text-sm text-[#2E3A3F]" data-testid="child-color-name">
                      {COLOR_NAMES[(viewing.color || COLOR_PALETTE[0]).toUpperCase()] ||
                        COLOR_NAMES[viewing.color || COLOR_PALETTE[0]] ||
                        "Custom"}
                    </span>
                    {canManageViewing && (
                      <span className="text-[11px] text-[#6F828A] ml-auto">Tap Edit to change</span>
                    )}
                  </div>
                </div>

                {/* v66 — per-child co-parents panel. Lists who's linked
                    to this specific child. Primary parent can toggle
                    co-parent access in-place; co-parents see a read-only
                    summary. Each child can have its own set of
                    co-parents — Mia might be co-parented by Sam while
                    Noah is co-parented by Carlos. */}
                <div className="border border-[#D9E1E3] rounded-sm p-3" data-testid="child-coparents-panel">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2 inline-flex items-center gap-1.5">
                    <UserRound className="w-3.5 h-3.5" /> Co-parenting access
                    {!canManageViewing && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#6F828A]/80 ml-2">
                        <Lock className="w-3 h-3" /> view-only
                      </span>
                    )}
                  </div>
                  {!coparentsEditing ? (
                    <>
                      <ul className="space-y-1.5" data-testid="child-coparents-list">
                        {(coparentsByChild[viewing.id] || []).map((cp) => {
                          // v77.19 — Each co-parent gets their OWN colour
                          // (read from the persisted parent_color field).
                          // Falls back to a deterministic palette pick so
                          // the dot/initial circle is always coloured.
                          const cpColor = getParentColor(cp);
                          return (
                          <li key={cp.id} className="flex items-center gap-2 text-sm">
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold text-white"
                              style={{ backgroundColor: cpColor }}
                              data-testid={`coparent-avatar-${cp.id}`}
                            >
                              {(cp.name || "?").trim().slice(0, 1).toUpperCase()}
                            </span>
                            <span className="font-medium">{cp.name}</span>
                            {cp.is_primary_parent && (
                              <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-[#1F9FA4] bg-[#E0F2F2] px-1.5 py-0.5 rounded-sm">
                                Primary
                              </span>
                            )}
                          </li>
                          );
                        })}
                        {(coparentsByChild[viewing.id] || []).length === 0 && (
                          <li className="text-[12px] text-[#6F828A]">No co-parents linked to {viewing.name} yet.</li>
                        )}
                      </ul>
                      {canManageViewing && householdMembers.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={startEditCoparents}
                          className="mt-3 border-[#D9E1E3] rounded-sm"
                          data-testid="child-coparents-edit"
                        >
                          <Pencil className="w-3 h-3 mr-1.5" /> Manage co-parent access
                        </Button>
                      )}
                      {/* v72 — primary parent only. Generate a per-
                          child invite token and (optionally) send it
                          via email. Defaults to a single child
                          (locked decision); siblings can be added in
                          the dialog. */}
                      {canManageViewing && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={openInvite}
                          className="mt-2 border-[#A7D8DA] text-[#116B6E] rounded-sm hover:bg-[#E0F2F2] w-full"
                          data-testid="child-invite-coparent-button"
                        >
                          <UserRound className="w-3 h-3 mr-1.5" /> Invite co-parent for {viewing.name}
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2" data-testid="child-coparents-editor">
                      <p className="text-[11px] text-[#6F828A]">
                        Tap to toggle which household members can see {viewing.name}&apos;s data. The managing parent always has access.
                      </p>
                      <div className="space-y-1.5">
                        {householdMembers
                          .filter((m) => m.id !== user?.id)
                          .map((m) => {
                            const checked = coparentsDraft.includes(m.id);
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => toggleCoparentDraft(m.id)}
                                className={`w-full flex items-center gap-2 text-sm border rounded-sm p-2 text-left transition-colors ${checked ? "border-[#1F9FA4] bg-[#E0F2F2]/40" : "border-[#D9E1E3] hover:border-[#A7D8DA]"}`}
                                data-testid={`child-coparents-toggle-${m.id}`}
                              >
                                <span className={`w-4 h-4 rounded-sm border flex-shrink-0 ${checked ? "bg-[#1F9FA4] border-[#1F9FA4]" : "border-[#A7C5DA]"}`}>
                                  {checked && <span className="block text-white text-[10px] leading-none text-center mt-[1px]">✓</span>}
                                </span>
                                <span className="font-medium">{m.name}</span>
                                <span className="text-[10px] text-[#6F828A] ml-auto">{m.email}</span>
                              </button>
                            );
                          })}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCoparentsEditing(false)}
                          className="border-[#D9E1E3] rounded-sm flex-1"
                          data-testid="child-coparents-cancel"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={saveCoparents}
                          className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm flex-1"
                          data-testid="child-coparents-save"
                        >
                          Save access
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="border border-[#F3D4D9] bg-[#FDF5F6] rounded-sm p-3 space-y-3" data-testid="child-medical-block">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#8C2A36] font-medium inline-flex items-center gap-1.5 w-full">
                    <HeartPulse className="w-3.5 h-3.5" /> Medical
                    <SectionLastUpdated section="medical" sectionsForChild={sectionsByChild[viewing.id]} />
                  </div>
                  <InfoRow icon={AlertTriangle} label="Allergies" value={viewing.allergies} testid="child-view-allergies" />
                  <InfoRow icon={Pill} label="Medication" value={viewing.medication} testid="child-view-medication" />
                  <InfoRow icon={StickyNote} label="Medication instructions" value={viewing.medication_instructions} testid="child-view-medication-instructions" />
                  <InfoRow icon={Droplet} label="Blood type" value={viewing.blood_type} testid="child-view-blood-type" />
                  <InfoRow icon={HeartPulse} label="Other medical notes" value={viewing.medical} />
                  {(viewing.gp_name || viewing.gp_address || viewing.gp_phone) && (
                    <div className="pt-2 mt-1 border-t border-[#F3D4D9]/60 space-y-3" data-testid="child-view-gp-block">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-[#8C2A36]/80 font-medium">GP / Doctor</div>
                      <InfoRow icon={Stethoscope} label="GP name" value={viewing.gp_name} testid="child-view-gp-name" />
                      <InfoRow icon={MapPin} label="GP address" value={viewing.gp_address} testid="child-view-gp-address" />
                      <InfoRow icon={Phone} label="GP phone number" value={viewing.gp_phone} testid="child-view-gp-phone" />
                    </div>
                  )}
                  <InfoRow icon={IdCard} label="Medical notes" value={viewing.medical_notes} testid="child-view-medical-notes" />
                </div>
                <div className="border border-[#C8E0D4] bg-[#F1F8F4] rounded-sm p-3 space-y-3" data-testid="child-school-block">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#1F6A47] font-medium inline-flex items-center gap-1.5 w-full">
                    <School className="w-3.5 h-3.5" /> School
                    <SectionLastUpdated section="school" sectionsForChild={sectionsByChild[viewing.id]} />
                  </div>
                  <InfoRow icon={School} label="School name" value={viewing.school} testid="child-view-school" />
                  <InfoRow icon={UserRound} label="Teacher's name" value={viewing.teacher_name} testid="child-view-teacher-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <InfoRow icon={Clock} label="Drop-off time" value={viewing.school_dropoff_time} testid="child-view-dropoff" />
                    <InfoRow icon={Clock} label="Pick-up time" value={viewing.school_pickup_time} testid="child-view-pickup" />
                  </div>
                  <InfoRow icon={Phone} label="School contact phone" value={viewing.school_phone} testid="child-view-school-phone" />
                  <InfoRow icon={StickyNote} label="School notes / instructions" value={viewing.school_notes} testid="child-view-school-notes" />
                </div>
                <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-3" data-testid="child-emergency-block">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5 w-full">
                    <PhoneCall className="w-3.5 h-3.5" /> Emergency contact
                    <SectionLastUpdated section="emergency" sectionsForChild={sectionsByChild[viewing.id]} />
                  </div>
                  <InfoRow icon={PhoneCall} label="Emergency contact" value={viewing.emergency_contact} testid="child-view-emergency-contact" />
                </div>
                <InfoRow icon={Sparkles} label="Routines" value={viewing.routines} />
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow icon={Shirt} label="Clothing size" value={viewing.clothing_size} testid="child-view-clothing" />
                  <InfoRow icon={Footprints} label="Shoe size" value={viewing.shoe_size} testid="child-view-shoe" />
                </div>
                <InfoRow icon={StickyNote} label="Notes" value={viewing.notes} />
                {!hasAnyDetail(viewing) && (
                  <p className="text-sm text-[#6F828A] italic">No details recorded yet. Tap Edit to add some.</p>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="ghost" onClick={() => setViewing(null)} data-testid="child-view-close-button"><ChevronLeft className="w-4 h-4 mr-1" /> Close</Button>
                <Button
                  variant="outline"
                  onClick={() => openHistory(viewing)}
                  className="border-[#D9E1E3] rounded-sm"
                  data-testid="child-view-history-button"
                >
                  <History className="w-4 h-4 mr-1.5" /> View history
                </Button>
                {canManageViewing && !viewing.archived_at && (
                  <Button
                    variant="outline"
                    onClick={() => setConfirmAction({ kind: "archive", child: viewing })}
                    className="border-[#D9E1E3] rounded-sm text-[#8C2A36] hover:bg-[#FDF5F6]"
                    data-testid="child-view-archive-button"
                  >
                    <Archive className="w-4 h-4 mr-1.5" /> Archive
                  </Button>
                )}
                {canManageViewing && viewing.archived_at && (
                  <Button
                    variant="outline"
                    onClick={() => setConfirmAction({ kind: "restore", child: viewing })}
                    className="border-[#A6D7D9] rounded-sm text-[#116B6E] hover:bg-[#E0F2F2]"
                    data-testid="child-view-restore-button"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore
                  </Button>
                )}
                {!viewing.archived_at && (
                  <Button onClick={() => openEdit(viewing)} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid={`child-view-edit-button`}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit profile
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT / NEW dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) tryCloseEdit(); }}>
        <DialogContent
          preventOutsideClose
          className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto max-w-2xl"
          onEscapeKeyDown={(e) => {
            if (isFormDirty()) {
              e.preventDefault();
              tryCloseEdit();
            }
          }}
        >
          <DialogHeader><DialogTitle className="font-heading">{editing?.isNew ? "Add child" : `Edit ${editing?.name || "profile"}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="child-name-input" /></div>
              <div><Label>Date of birth</Label><Input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="child-dob-input" /></div>
            </div>
            <div>
              <Label>Colour</Label>
              {!canManageEditing ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-[#6F828A]">
                  <span className="w-6 h-6 rounded-full ring-1 ring-[#D9E1E3]" style={{ backgroundColor: form.color || COLOR_PALETTE[0] }} />
                  <span className="inline-flex items-center gap-1 text-[11px]">
                    <Lock className="w-3 h-3" /> Only the managing parent can change this.
                  </span>
                </div>
              ) : (
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="child-color-picker">
                  {COLOR_PALETTE.map((c) => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? "border-[#2E3A3F] scale-110" : "border-white hover:border-[#D9E1E3]"}`}
                      style={{ backgroundColor: c }} aria-label={`Colour ${c}`} data-testid={`child-color-option-${c}`} />
                  ))}
                </div>
              )}
            </div>
            {/* v77.99.9 — Parenting arrangement (managing-parent only).
                Empty value = inherit from household default / data-derived. */}
            <div data-testid="child-section-arrangement">
              <Label>Parenting arrangement</Label>
              <p className="text-[11.5px] text-[#6F828A] mt-1 mb-2 leading-relaxed">
                Tells the dashboard whether to show schedule prompts for this child. Leave on "Auto"
                to inherit the household default — pick a specific arrangement to override.
              </p>
              {!canManageEditing ? (
                <div className="flex items-center gap-2 text-sm text-[#6F828A]">
                  <span className="font-medium text-[#0B2E32]">
                    {ARRANGEMENT_LABEL[form.parenting_arrangement] || "Auto (inherit household default)"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px]">
                    <Lock className="w-3 h-3" /> Only the managing parent can change this.
                  </span>
                </div>
              ) : (
                <Select
                  value={form.parenting_arrangement || "__auto__"}
                  onValueChange={(v) => setForm({ ...form, parenting_arrangement: v === "__auto__" ? "" : v })}
                >
                  <SelectTrigger data-testid="child-arrangement-trigger">
                    <SelectValue placeholder="Auto (inherit household default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__" data-testid="child-arrangement-option-auto">
                      Auto · inherit household default
                    </SelectItem>
                    {ARRANGEMENT_TYPES.map((a) => (
                      <SelectItem
                        key={a.value}
                        value={a.value}
                        data-testid={`child-arrangement-option-${a.value}`}
                      >
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.parenting_arrangement && (
                <p className="text-[11.5px] text-[#4A5560] mt-2 italic">
                  {ARRANGEMENT_TYPES.find((a) => a.value === form.parenting_arrangement)?.description}
                </p>
              )}
            </div>
            <div className="border border-[#F3D4D9] bg-[#FDF5F6] rounded-sm p-3 space-y-3" data-testid="child-section-medical">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#8C2A36] font-medium inline-flex items-center gap-1.5">
                  <HeartPulse className="w-3.5 h-3.5" /> Medical
                  <span className="text-[#8C2A36]/70 font-normal normal-case tracking-normal text-[11px]">· all fields optional</span>
                  {!canManageEditing && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#FDE7EC] text-[#8C2A36] text-[10px] uppercase tracking-[0.08em] font-medium border border-[#F3D4D9]">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                {!canManageEditing && editing && !editing.isNew && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openSuggestUpdate(editing, "medical")}
                    className="border-[#8C2A36]/40 text-[#8C2A36] hover:bg-[#FDE7EC] h-7 text-xs"
                    data-testid="child-suggest-medical-button"
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Suggest update
                  </Button>
                )}
              </div>
              {!canManageEditing && (
                <p className="text-[11px] text-[#8C2A36]/80 -mt-1">
                  Only the managing parent can change medical fields. Use “Suggest update” to propose a change for them to review.
                </p>
              )}
              <div><Label>Allergies <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={2} value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="e.g. peanuts, penicillin" disabled={!canManageEditing} data-testid="child-allergies-input" /></div>
              <div><Label>Medication <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} placeholder="e.g. Ventolin inhaler" disabled={!canManageEditing} data-testid="child-medication-input" /></div>
              <div><Label>Medication instructions <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={3} value={form.medication_instructions} onChange={(e) => setForm({ ...form, medication_instructions: e.target.value })} placeholder="Dosage, timing, triggers, how to administer…" disabled={!canManageEditing} data-testid="child-medication-instructions-input" /></div>
              <div>
                <Label>Blood type <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label>
                <Select value={form.blood_type || ""} onValueChange={(v) => setForm({ ...form, blood_type: v === "__none__" ? "" : v })} disabled={!canManageEditing}>
                  <SelectTrigger data-testid="child-blood-type-select"><SelectValue placeholder="Select blood type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not recorded</SelectItem>
                    {BLOOD_TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`child-blood-type-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Other medical notes <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={2} value={form.medical} onChange={(e) => setForm({ ...form, medical: e.target.value })} disabled={!canManageEditing} data-testid="child-medical-input" /></div>
              <div className="pt-3 mt-1 border-t border-[#F3D4D9]/60 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#8C2A36]/80 font-medium">GP / Doctor</div>
                <div><Label>GP name <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.gp_name} onChange={(e) => setForm({ ...form, gp_name: e.target.value })} placeholder="e.g. Dr Sarah Malone" disabled={!canManageEditing} data-testid="child-gp-name-input" /></div>
                <div><Label>GP address <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={2} value={form.gp_address} onChange={(e) => setForm({ ...form, gp_address: e.target.value })} placeholder="Surgery or practice address" disabled={!canManageEditing} data-testid="child-gp-address-input" /></div>
                <div><Label>GP phone number <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input type="tel" value={form.gp_phone} onChange={(e) => setForm({ ...form, gp_phone: e.target.value })} placeholder="e.g. 020 7946 0456" disabled={!canManageEditing} data-testid="child-gp-phone-input" /></div>
              </div>
              <div><Label>Medical notes <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={3} value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} placeholder="e.g. Medical card number, GP visit card, or medical insurance information" disabled={!canManageEditing} data-testid="child-medical-notes-input" /></div>
            </div>
            <div className="border border-[#C8E0D4] bg-[#F1F8F4] rounded-sm p-3 space-y-3" data-testid="child-section-school">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#1F6A47] font-medium inline-flex items-center gap-1.5">
                  <School className="w-3.5 h-3.5" /> School
                  <span className="text-[#1F6A47]/70 font-normal normal-case tracking-normal text-[11px]">· all fields optional</span>
                  {!canManageEditing && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#E0F0E6] text-[#1F6A47] text-[10px] uppercase tracking-[0.08em] font-medium border border-[#C8E0D4]">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                {!canManageEditing && editing && !editing.isNew && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openSuggestUpdate(editing, "school")}
                    className="border-[#1F6A47]/40 text-[#1F6A47] hover:bg-[#E0F0E6] h-7 text-xs"
                    data-testid="child-suggest-school-button"
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Suggest update
                  </Button>
                )}
              </div>
              {!canManageEditing && (
                <p className="text-[11px] text-[#1F6A47]/80 -mt-1">
                  Only the managing parent can change school information. Use “Suggest update” to propose a change.
                </p>
              )}
              <div><Label>School name <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} placeholder="e.g. Oakfield Primary" disabled={!canManageEditing} data-testid="child-school-name-input" /></div>
              <div><Label>Teacher's name <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.teacher_name} onChange={(e) => setForm({ ...form, teacher_name: e.target.value })} placeholder="e.g. Ms Doyle" disabled={!canManageEditing} data-testid="child-teacher-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Drop-off time <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input type="time" value={form.school_dropoff_time} onChange={(e) => setForm({ ...form, school_dropoff_time: e.target.value })} disabled={!canManageEditing} data-testid="child-school-dropoff-input" /></div>
                <div><Label>Pick-up time <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input type="time" value={form.school_pickup_time} onChange={(e) => setForm({ ...form, school_pickup_time: e.target.value })} disabled={!canManageEditing} data-testid="child-school-pickup-input" /></div>
              </div>
              <div><Label>School contact phone <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input type="tel" value={form.school_phone} onChange={(e) => setForm({ ...form, school_phone: e.target.value })} placeholder="e.g. 020 7946 0123" disabled={!canManageEditing} data-testid="child-school-phone-input" /></div>
              <div><Label>School notes / instructions <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={3} value={form.school_notes} onChange={(e) => setForm({ ...form, school_notes: e.target.value })} placeholder="Collection rules, authorised persons, special arrangements…" disabled={!canManageEditing} data-testid="child-school-notes-input" /></div>
            </div>
            <div><Label>Routines <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={2} value={form.routines} onChange={(e) => setForm({ ...form, routines: e.target.value })} data-testid="child-routines-input" /></div>
            <div className="border border-[#E5C8A6] bg-[#FAF5EE] rounded-sm p-3 space-y-3" data-testid="child-section-emergency">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#825A41] font-medium inline-flex items-center gap-1.5">
                  <PhoneCall className="w-3.5 h-3.5" /> Emergency
                  {!canManageEditing && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#F1E2CB] text-[#825A41] text-[10px] uppercase tracking-[0.08em] font-medium border border-[#E5C8A6]">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                {!canManageEditing && editing && !editing.isNew && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openSuggestUpdate(editing, "emergency")}
                    className="border-[#825A41]/40 text-[#825A41] hover:bg-[#F1E2CB] h-7 text-xs"
                    data-testid="child-suggest-emergency-button"
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Suggest update
                  </Button>
                )}
              </div>
              <div><Label>Emergency contact <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} disabled={!canManageEditing} data-testid="child-emergency-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Clothing size <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.clothing_size} onChange={(e) => setForm({ ...form, clothing_size: e.target.value })} placeholder="e.g. 7–8 yrs" data-testid="child-clothing-input" /></div>
              <div><Label>Shoe size <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Input value={form.shoe_size} onChange={(e) => setForm({ ...form, shoe_size: e.target.value })} placeholder="e.g. UK 2" data-testid="child-shoe-input" /></div>
            </div>
            <div><Label>Notes <span className="text-[#6F828A] font-normal text-xs">(optional)</span></Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="child-notes-input" /></div>
            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={tryCloseEdit} data-testid="child-cancel-button">Cancel</Button>
              <Button type="submit" disabled={savingChild} className="bg-[#1F9FA4] hover:bg-[#178085] disabled:opacity-60 disabled:cursor-not-allowed" data-testid="child-save-button">{savingChild ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* v69 — Change history dialog. Both members can read. The primary
          parent gets a "Restore" button on each field-level entry. */}
      <Dialog open={!!historyOpenFor} onOpenChange={(open) => !open && setHistoryOpenFor(null)}>
        <DialogContent
          className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto"
          aria-describedby="history-desc"
          data-testid="child-history-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              <History className="w-5 h-5" /> Change history
            </DialogTitle>
            <p id="history-desc" className="text-xs text-[#6F828A] mt-1">
              Every change to this profile is recorded — who, what, when. This helps both parents stay aligned and creates a reliable record.
            </p>
          </DialogHeader>
          {historyLoading && (
            <div className="text-sm text-[#6F828A] py-8 text-center" data-testid="child-history-loading">Loading history…</div>
          )}
          {!historyLoading && historyOpenFor && (() => {
            const rows = historyByChild[historyOpenFor] || [];
            if (rows.length === 0) {
              return (
                <div className="text-sm text-[#6F828A] py-8 text-center italic" data-testid="child-history-empty">
                  No changes recorded yet.
                </div>
              );
            }
            const childObj = kids.find((c) => c.id === historyOpenFor);
            return (
              <ol className="space-y-3 mt-2" data-testid="child-history-list">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    className="border border-[#D9E1E3] rounded-sm p-3 bg-[#FAFBFB]"
                    data-testid={`child-history-entry-${r.id}`}
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">
                        {SECTION_LABELS[r.section] || r.section}
                      </span>
                      <span className="text-sm font-medium" data-testid={`child-history-action-${r.id}`}>
                        {r.action === "created" && "Profile created"}
                        {r.action === "archived" && "Profile archived"}
                        {r.action === "restored" && "Profile restored"}
                        {(r.action === "updated" || r.action === "reverted") && (r.label || "Field updated")}
                      </span>
                      <span className="text-[11px] text-[#6F828A] font-mono-data ml-auto">
                        {formatHistoryAt(r.at)}
                      </span>
                    </div>
                    {r.action === "updated" || r.action === "reverted" ? (
                      <div className="mt-2 text-sm flex items-center gap-2 flex-wrap">
                        <HistoryValue field={r.field} value={r.old_value} />
                        <span className="text-[#6F828A]">→</span>
                        <HistoryValue field={r.field} value={r.new_value} />
                      </div>
                    ) : null}
                    <div className="mt-2 text-[11px] text-[#6F828A] flex items-center gap-2 flex-wrap">
                      <span>by <span className="text-[#2E3A3F] font-medium">{r.by_name || "—"}</span></span>
                      {r.action === "reverted" && r.reverted_from && (
                        <span className="inline-flex items-center gap-1 text-[#116B6E]">
                          <RotateCcw className="w-3 h-3" /> reverted earlier change
                        </span>
                      )}
                      {isManagingParentOf(childObj) && (r.action === "updated" || r.action === "reverted") && r.field && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmAction({ kind: "revert", child: childObj, entry: r })}
                          className="ml-auto h-7 text-xs hover:bg-[#EEF3F4]"
                          data-testid={`child-history-revert-${r.id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore previous
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            );
          })()}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setHistoryOpenFor(null)} data-testid="child-history-close-button">
              Close
            </Button>
            {historyOpenFor && (
              <Button
                variant="outline"
                onClick={() => downloadHistoryPdf(historyOpenFor)}
                className="border-[#D9E1E3] rounded-sm"
                data-testid="child-history-download-pdf-button"
              >
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3 Delivery 2 — "Suggest update" dialog. Co-parents
          submit proposed changes here for the primary parent to
          review. We render one input per field in the locked section
          + a single shared note. Submit creates one proposal per
          field that actually changed. */}
      <Dialog open={!!proposalChild && !!proposalSection} onOpenChange={(open) => !open && closeSuggestUpdate()}>
        <DialogContent
          className="bg-white max-w-lg"
          aria-describedby="child-suggest-desc"
          data-testid="child-suggest-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> Suggest update
            </DialogTitle>
            <p id="child-suggest-desc" className="text-xs text-[#6F828A] mt-1">
              Propose a change to {proposalChild?.name || "this child"}&apos;s {LOCKED_SECTION_LABELS[proposalSection] || proposalSection} section. The managing parent will review your suggestion before it&apos;s applied. Both of you will see the change in the history.
            </p>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {(LOCKED_SECTION_FIELDS[proposalSection] || []).map((f) => (
              <div key={f}>
                <Label className="text-xs">{LOCKED_FIELD_LABELS[f] || f}</Label>
                <div className="text-[11px] text-[#6F828A] mb-1">Current: <span className="text-[#2E3A3F]">{(proposalChild?.[f] || "—") || "—"}</span></div>
                <Input
                  value={proposalDrafts[f]?.new_value || ""}
                  onChange={(e) => setProposalDrafts((prev) => ({ ...prev, [f]: { new_value: e.target.value } }))}
                  placeholder="Suggested new value (leave blank to clear)"
                  data-testid={`child-suggest-${f}-input`}
                />
              </div>
            ))}
            <div className="pt-1">
              <Label className="text-xs">Note for the managing parent <span className="text-[#6F828A] font-normal">(optional)</span></Label>
              <Textarea
                rows={2}
                value={proposalNote}
                onChange={(e) => setProposalNote(e.target.value)}
                placeholder="Why you'd like this change. Keep it short and neutral."
                data-testid="child-suggest-note-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={closeSuggestUpdate} data-testid="child-suggest-cancel-button">Cancel</Button>
            <Button
              onClick={submitProposals}
              disabled={proposalSubmitting}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="child-suggest-send-button"
            >
              {proposalSubmitting ? "Sending…" : "Send suggestion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3 Delivery 2 — primary-parent proposal review dialog.
          Lists every pending proposal across all children. Approve
          applies the change (logged to history); decline marks the
          proposal rejected with an optional note. */}
      <ChildProposalsReviewDialog
        open={proposalReviewOpen}
        onOpenChange={setProposalReviewOpen}
        pendingProposals={pendingProposals}
        proposalDecisionBusy={proposalDecisionBusy}
        decideProposal={decideProposal}
      />

      <div className="hidden">{/* Phase 3 Delivery 2 — placeholder for old DialogContent close */}</div>

      {/* v69 — Confirm dialog for destructive actions (archive / restore /
          revert). Neutral, non-accusatory copy per safeguarding spec.
          Always reminds the user the action is logged and visible. */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent
          className="bg-white max-w-md"
          aria-describedby="child-confirm-desc"
          data-testid="child-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              {confirmAction?.kind === "archive" && (<><Archive className="w-5 h-5" /> Archive this profile?</>)}
              {confirmAction?.kind === "restore" && (<><ArchiveRestore className="w-5 h-5" /> Restore this profile?</>)}
              {confirmAction?.kind === "revert" && (<><RotateCcw className="w-5 h-5" /> Restore previous value?</>)}
            </DialogTitle>
          </DialogHeader>
          <div id="child-confirm-desc" className="space-y-3 text-sm">
            {confirmAction?.kind === "archive" && (
              <>
                <p>{confirmAction.child?.name}&apos;s profile will be moved to the archived list. Their data — including history, linked events, expenses, and journal entries — stays exactly as it is and remains visible. You can restore the profile at any time.</p>
                <p className="text-[#6F828A] text-xs inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> This action is logged and visible to the other parent.
                </p>
              </>
            )}
            {confirmAction?.kind === "restore" && (
              <>
                <p>{confirmAction.child?.name}&apos;s profile will return to the active list and become editable again.</p>
                <p className="text-[#6F828A] text-xs inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> This action is logged and visible to the other parent.
                </p>
              </>
            )}
            {confirmAction?.kind === "revert" && (
              <>
                <p>
                  Restore <span className="font-medium">{confirmAction.entry?.label}</span> to the previous value?
                </p>
                <div className="border border-[#D9E1E3] rounded-sm p-2 bg-[#FAFBFB] text-xs flex items-center gap-2 flex-wrap">
                  <HistoryValue field={confirmAction.entry?.field} value={confirmAction.entry?.new_value} />
                  <span className="text-[#6F828A]">→</span>
                  <HistoryValue field={confirmAction.entry?.field} value={confirmAction.entry?.old_value} />
                </div>
                <p className="text-[#6F828A] text-xs inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> The restore is logged as a separate entry — the original change stays in the history.
                </p>
              </>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setConfirmAction(null)} data-testid="child-confirm-cancel">Cancel</Button>
            {confirmAction?.kind === "archive" && (
              <Button
                onClick={() => archiveChild(confirmAction.child)}
                disabled={archiving}
                className="bg-[#8C2A36] hover:bg-[#6F2129] text-white"
                data-testid="child-confirm-archive"
              >
                <Archive className="w-4 h-4 mr-1.5" /> {archiving ? "Archiving…" : "Archive profile"}
              </Button>
            )}
            {confirmAction?.kind === "restore" && (
              <Button
                onClick={() => restoreChild(confirmAction.child)}
                disabled={archiving}
                className="bg-[#1F9FA4] hover:bg-[#178085] text-white"
                data-testid="child-confirm-restore"
              >
                <ArchiveRestore className="w-4 h-4 mr-1.5" /> {archiving ? "Restoring…" : "Restore profile"}
              </Button>
            )}
            {confirmAction?.kind === "revert" && (
              <Button
                onClick={() => revertChange(confirmAction.child, confirmAction.entry)}
                className="bg-[#1F9FA4] hover:bg-[#178085] text-white"
                data-testid="child-confirm-revert"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Restore previous
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v72 — Per-child invite dialog. Primary parent only. */}
      <InviteCoparentDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        viewing={viewing}
        kids={kids}
        inviteChildIds={inviteChildIds}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteResult={inviteResult}
        inviteSending={inviteSending}
        submitInvite={submitInvite}
        copyInviteLink={copyInviteLink}
        toggleInviteChild={toggleInviteChild}
      />
    </div>
  );
}

function hasAnyDetail(c) {
  return !!(c.school || c.teacher_name || c.medical || c.medical_notes || c.allergies || c.medication || c.medication_instructions || c.gp_name || c.gp_address || c.gp_phone || c.routines || c.emergency_contact || c.clothing_size || c.shoe_size || c.notes || c.dob);
}

const InfoRow = ({ icon: Icon, label, value, testid }) => {
  if (!value) {
    return (
      <div className="flex items-start gap-3" data-testid={testid}>
        <Icon className="w-4 h-4 text-[#A5B4BB] mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
          <div className="text-sm text-[#A5B4BB] italic">Not recorded</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#6F828A] mt-0.5" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
        <div className="text-[#2E3A3F] whitespace-pre-wrap">{value}</div>
      </div>
    </div>
  );
};
